import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, ArrowRight, Search, Filter, Grid, List } from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicEventsPage = () => {
  const [content, setContent] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [eventsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/cms/events`),
          axios.get(`${API}/cms/settings`)
        ]);
        setContent(eventsRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Failed to load events content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const { events, filters } = content || {};
  
  const filteredEvents = events?.filter(event => {
    const matchesSearch = event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSport = selectedSport === 'all' || event.sport === selectedSport;
    const matchesType = selectedType === 'all' || event.event_type === selectedType;
    return matchesSearch && matchesSport && matchesType;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <MegaMenu />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-primary/10 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-block px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-medium mb-6">
              Sports Calendar
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Upcoming Sports<br />Events
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto">
              Discover major sporting events, tournaments, and competitions 
              happening across Africa. Stay informed and never miss the action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-card-bg border-y border-border sticky top-16 lg:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-background border border-border rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary"
                data-testid="events-search"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                className="px-4 py-2.5 bg-background border border-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                data-testid="sport-filter"
              >
                <option value="all">All Sports</option>
                {filters?.sports?.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2.5 bg-background border border-border rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                data-testid="type-filter"
              >
                <option value="all">All Types</option>
                {filters?.event_types?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              {/* View Toggle */}
              <div className="flex items-center bg-background border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2.5 ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-white/60 hover:text-white'}`}
                  data-testid="grid-view"
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2.5 ${viewMode === 'list' ? 'bg-primary text-white' : 'text-white/60 hover:text-white'}`}
                  data-testid="list-view"
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Events List */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* SomaBay Featured Event */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Link 
              to="/somabay"
              className="block group relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-900 via-teal-800 to-cyan-900 border border-cyan-600/30 hover:border-cyan-500/50 transition-all"
              data-testid="somabay-featured-event"
            >
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }} />
              <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
                {/* Icon/Badge */}
                <div className="flex-shrink-0 w-20 h-20 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center">
                  <svg className="w-10 h-10 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                    <path d="M8 21l2-3m6 3l-2-3" strokeLinecap="round" />
                  </svg>
                </div>
                
                {/* Content */}
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-2">
                    <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 text-xs font-semibold rounded-full">
                      FEATURED
                    </span>
                    <span className="px-3 py-1 bg-white/10 text-white/80 text-xs rounded-full">
                      Golf
                    </span>
                    <span className="px-3 py-1 bg-white/10 text-white/80 text-xs rounded-full">
                      International
                    </span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                    SomaBay World Cup 2026
                  </h3>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 text-white/70 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      October 11-16, 2026
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      Soma Bay, Egypt
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      20+ Countries
                    </span>
                  </div>
                </div>
                
                {/* CTA */}
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl transition-colors">
                    View Details <ArrowRight size={18} />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>

          {viewMode === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents?.map((event, idx) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  viewport={{ once: true }}
                  className="group bg-card-bg border border-border rounded-xl overflow-hidden hover:border-primary/50 transition-all"
                  data-testid={`event-card-${event.id}`}
                >
                  {/* Image */}
                  <div className="h-36 bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center relative">
                    <Calendar className="w-12 h-12 text-white/10" />
                    {event.sport && (
                      <span className="absolute top-3 left-3 px-2 py-1 bg-black/60 backdrop-blur-sm text-primary text-xs font-medium rounded">
                        {event.sport}
                      </span>
                    )}
                    {event.status && (
                      <span className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium rounded ${
                        event.status === 'upcoming' ? 'bg-green-500/20 text-green-400' :
                        event.status === 'ongoing' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {event.status}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 text-xs text-white/50 mb-2">
                      <Clock size={12} />
                      {formatDate(event.start_date)}
                    </div>
                    <h3 className="text-lg font-heading font-bold text-white mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {event.name}
                    </h3>
                    {event.description && (
                      <p className="text-white/60 text-sm line-clamp-2 mb-3">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs text-white/50">
                      {event.event_type && <span>{event.event_type}</span>}
                      {event.venue && (
                        <span className="flex items-center gap-1">
                          <MapPin size={10} />
                          {event.venue}
                        </span>
                      )}
                      {event.capacity && (
                        <span className="flex items-center gap-1">
                          <Users size={10} />
                          {event.capacity}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredEvents?.map((event, idx) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  viewport={{ once: true }}
                  className="flex flex-col sm:flex-row gap-4 bg-card-bg border border-border rounded-xl p-4 hover:border-primary/50 transition-all"
                  data-testid={`event-list-${event.id}`}
                >
                  {/* Date Box */}
                  <div className="flex-shrink-0 w-20 h-20 bg-primary/10 border border-primary/30 rounded-xl flex flex-col items-center justify-center">
                    {event.start_date ? (
                      <>
                        <span className="text-primary text-xs font-medium uppercase">
                          {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short' })}
                        </span>
                        <span className="text-white text-2xl font-heading font-bold">
                          {new Date(event.start_date).getDate()}
                        </span>
                      </>
                    ) : (
                      <span className="text-white/40 text-xs">TBA</span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {event.sport && (
                        <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">{event.sport}</span>
                      )}
                      {event.event_type && (
                        <span className="px-2 py-0.5 bg-white/5 text-white/60 text-xs rounded">{event.event_type}</span>
                      )}
                    </div>
                    <h3 className="text-lg font-heading font-bold text-white mb-1">{event.name}</h3>
                    {event.description && (
                      <p className="text-white/60 text-sm line-clamp-1 mb-2">{event.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-white/50">
                      {event.venue && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} />
                          {event.venue}
                        </span>
                      )}
                      {event.location && (
                        <span>{event.location}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {(!filteredEvents || filteredEvents.length === 0) && (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-white/10 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold text-white mb-2">No Events Found</h3>
              <p className="text-white/60">Try adjusting your search or filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary/20 via-background to-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Host Your Event with Michezonet
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Get end-to-end event management services including ticketing, 
            accreditation, and live coverage for your sports event.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl transition-all"
            data-testid="host-event-cta"
          >
            Get Started <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer settings={settings} />
    </div>
  );
};

export default PublicEventsPage;
